const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const NHL_DATA_FUNCTION_PATH = '/functions/v1/nhl-data';
const EDGE_BASE = 'https://api-web.nhle.com/v1/edge';

async function proxyFetch<T>(nhlUrl: string): Promise<T> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) throw new Error('Supabase config missing');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(`${SUPABASE_URL}${NHL_DATA_FUNCTION_PATH}?endpoint=${encodeURIComponent(nhlUrl)}`, {
      signal: controller.signal,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: 'Bearer ' + SUPABASE_ANON_KEY,
      },
    });
    if (!res.ok) {
      let body = '';
      try {
        body = await res.text();
      } catch {}
      // eslint-disable-next-line no-console
      console.error('nhlEdgeTeam proxy error', { nhlUrl, status: res.status, body });
      throw new Error(`NHL API ${res.status}: ${body}`);
    }
    const data = (await res.json()) as T;
    return data;
  } finally {
    clearTimeout(timer);
  }
}

const abbrevCache = new Map<string, number>();

export async function resolveTeamIdFromAbbrev(abbrev: string): Promise<number | undefined> {
  const key = abbrev.toUpperCase();
  if (abbrevCache.has(key)) return abbrevCache.get(key);

  // Try to resolve by probing team-detail endpoints (1..50) via the api-web.nhle.com edge team-detail
  for (let id = 1; id <= 50; id++) {
    try {
      const path = `${EDGE_BASE}/team-detail/${id}/now`;
      const data = await proxyFetch<any>(path);
      const team = data?.team;
      const foundAbbrev = (team?.abbrev ?? team?.abbreviation ?? '').toString().toUpperCase();
      if (foundAbbrev === key) {
        abbrevCache.set(key, id);
        return id;
      }
    } catch (e) {
      // ignore errors and continue probing
    }
  }

  return undefined;
}

export async function fetchEdgeTeamDetail(teamId: number, season?: string, gameType: 2 | 3 = 2): Promise<any> {
  if (!Number.isInteger(teamId) || teamId <= 0) throw new Error('Invalid team id');
  const path = season ? `${EDGE_BASE}/team-detail/${teamId}/${season}/${gameType}` : `${EDGE_BASE}/team-detail/${teamId}/now`;
  return proxyFetch<any>(path);
}

export default fetchEdgeTeamDetail;
